import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { ITypeLocation } from "../models/typeLocation";
import { ITypeLocationApiResponse } from "../models/api/typeLocationApiResponse";
import { ITypeLocationFilter } from "../models/filters/typeLocationFilter";
import TypeLocationService from "../services/TypeLocationService";
import { ITypeLocationForm } from "../models/forms/typeLocationForm";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import { MountingPlanElements } from "../constantes/constants";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useTypeLocationStore = defineStore("typeLocations", () => {
    // State
    const datas = ref<ITypeLocation[]>([]);
    const current = ref<ITypeLocation>({} as ITypeLocation);
    const itemForm = ref<ITypeLocationForm>({} as ITypeLocationForm);
    const isLoading = ref<boolean>(false);

    // Getters
    const getListWithCustomTypes = computed((): ITypeLocation[] => {
        return [
            {
                // Faux ids inatteignables en prod pour gérer la sélection dans le 'EditBuildingDetail'
                id: 99000,
                backgroundColor: '',
                borderColor: '',
                description: 'Aucun',
                height: 0,
                width: 0,
                isIncremental: false,
                name: 'Aucun',
                isBoarAllowed: false,
                svgData: `
                    <svg width="34" height="33" viewBox="0 0 34 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.17 5L17 12.83L24.83 5L29 9.17L21.17 17L29 24.83L24.83 29L17 21.17L9.17 29L5 24.83L12.83 17L5 9.17L9.17 5Z" fill="#2B48A2"/>
                    </svg>

                `
            } as ITypeLocation,
            {
                id: 99001,
                backgroundColor: '',
                borderColor: '',
                description: 'Effacer',
                height: 0,
                width: 0,
                isIncremental: false,
                name: 'Effacer',
                isBoarAllowed: false,
                svgData: `
                    <svg width="34" height="33" viewBox="0 0 34 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path fill-rule="evenodd" clip-rule="evenodd" d="M1.01341 9.20094L8.63262 1.48474C9.98398 0.116172 12.1975 0.116172 13.5489 1.48474L22.1508 10.1961L9.61541 22.8911L1.01352 14.173C-0.337841 12.8088 -0.337951 10.5695 1.01341 9.20094ZM6.26586 32.5412H23.7025C24.6573 32.5412 25.4849 32.1942 26.1574 31.5132L32.2156 25.3777C33.567 24.0092 33.567 21.7675 32.2156 20.3989L23.6204 11.6808L11.0828 24.378L17.0705 30.4419L6.26597 30.4397C5.69264 30.4397 5.22928 30.909 5.22928 31.4896C5.22928 32.0724 5.69255 32.5412 6.26586 32.5412Z" fill="#2B48A2"/>
                    </svg>
                `
            } as ITypeLocation
        ].concat(datas.value);
    });

    const getTypeLocationNames = computed((): string[] => {
        return ['Aucun', 'Effacer'].concat(datas.value.map(tl => tl.name));
    });

    const getIdsWhereBoarsNotAllowed = computed((): Set<number> => {
        return new Set(datas.value.filter(lt => !lt.isBoarAllowed).map(lt => lt.id));
    });

    // Actions - Hydration
    async function hydrateTypeLocations(items: ITypeLocationApiResponse[]): Promise<ITypeLocation[]> {
        if (items.length === 0) return [];
        return items.map(item => ({ ...item } as unknown as ITypeLocation));
    }

    // Actions
    async function load(filter: ITypeLocationFilter) {
        isLoading.value = true;
        let result = await TypeLocationService.getAll<ITypeLocationFilter, ITypeLocationApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];
        if (!result.error) {
            datas.value = await hydrateTypeLocations(result.data as ITypeLocationApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await TypeLocationService.getById<ITypeLocationApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydrateTypeLocations([result.data as ITypeLocationApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<ITypeLocation | IDataOperationResult<ITypeLocation>>;
        if (itemForm.value.id > 0) {
            result = await TypeLocationService.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateTypeLocations([result.data as ITypeLocationApiResponse]);
                current.value = modelData;
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<ITypeLocation>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await TypeLocationService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateTypeLocations([result.data as ITypeLocationApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function deleteAction() {
        let result = await TypeLocationService.delete<ITypeLocationApiResponse>(current.value.id);
        if (!result.error) {
            let index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }
            current.value = newItem();
            itemForm.value = newForm();
        }
        return result;
    }

    function toForm(item: ITypeLocation): ITypeLocationForm {
        const form = {} as ITypeLocationForm;
        form.id = item.id;
        form.description = item.description;
        form.backgroundColor = item.backgroundColor;
        form.borderColor = item.borderColor;
        form.height = item.height;
        form.width = item.width;
        form.isIncremental = item.isIncremental;
        form.name = item.name;
        form.svgData = item.svgData;
        form.isBoarAllowed = item.isBoarAllowed;
        return form;
    }

    function newForm(): ITypeLocationForm {
        itemForm.value = {
            id: 0,
            description: "",
            backgroundColor: "",
            borderColor: "",
            height: 0,
            width: 0,
            isIncremental: false,
            name: "",
            svgData: "",
            isBoarAllowed: false,
        } as ITypeLocationForm;
        return itemForm.value;
    }

    function newItem(): ITypeLocation {
        current.value = {
            id: 0,
            description: "",
            backgroundColor: "",
            borderColor: "",
            height: 0,
            width: 0,
            isIncremental: false,
            name: "",
            svgData: "",
            isBoarAllowed: false,
        } as ITypeLocation;
        return current.value;
    }

    function reset() {
        current.value = getListWithCustomTypes.value.find(tl => tl.name == MountingPlanElements.NONE);
    }

    function setCurrentFromIndex(index: number) {
        current.value = getListWithCustomTypes.value[index];
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Getters
        getListWithCustomTypes,
        getTypeLocationNames,
        getIdsWhereBoarsNotAllowed,
        // Actions
        hydrateTypeLocations,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
        reset,
        setCurrentFromIndex
    };
});

signalRManager.connection.on("MajTypeLocation", async (payload: IPayloadStore) => {
    const store = useTypeLocationStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateTypeLocations([payload.data as ITypeLocationApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateTypeLocations([payload.data as ITypeLocationApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
